import { FEELING_COLORS3 } from '@/show/freeConfig';
import { Transmission, MiniScope } from './StageHud';

const BAND_COLOR = { NONE: 'var(--dim)', LOW: '#7f9cf5', MID: '#ff9ec7', HIGH: '#ffd166' };
const Row = ({ k, v, testId, warn }) => (
  <div className="flex justify-between gap-4 border-b border-white/5 py-0.5">
    <span className="text-[var(--muted)]">{k}</span>
    <span data-testid={testId} className={warn ? 'text-[var(--amber)]' : 'text-[var(--ink)]'}>{v}</span>
  </div>
);

export const LevelTracker = ({ snap }) => (
  <div className="flex flex-col items-end gap-3">
    <div className="label-eyebrow">FEELINGS DECODED <b data-testid="feelings-logged-count" className="text-[var(--ink)]">{snap.discovered.length}/{snap.feelings.length}</b></div>
    <div data-testid="comprehension-meter" className="w-full">
      <div className="label-eyebrow mb-1 text-right">LANGUAGE COMPREHENSION <b data-testid="comprehension-value" className="text-[var(--spore)]">{Math.round((snap.discovered.length / snap.feelings.length) * 100)}%</b></div>
      <span className="comp-meter"><span style={{ transform: `scaleX(${snap.discovered.length / snap.feelings.length})` }} /></span>
    </div>
    <div className="grid grid-cols-6 gap-2">
      {snap.feelings.map((f, i) => {
        const found = snap.discovered.includes(f);
        const current = snap.levelIdx === i && !found;
        return (
          <div key={f} data-testid={`logged-${f}`} data-found={found} className={`border px-2 py-1 text-[0.65rem] tracking-[0.15em] ${found ? '' : 'glitch text-[var(--dim)]'}`} style={found ? { borderColor: FEELING_COLORS3[f], color: FEELING_COLORS3[f] } : { borderColor: current ? 'var(--ink)' : 'var(--hair)' }}>
            {found ? `${i + 1} · ${f.toUpperCase()}` : `${i + 1} · ?????`}
          </div>
        );
      })}
    </div>
  </div>
);

export const SporeResponse = ({ snap }) => (
  <div data-testid="spore-response" className="stage-panel w-[340px] p-4">
    <div className="flex justify-between label-eyebrow"><span>SPORE<span className="normal-case">s</span> RESPONSE</span><b data-testid="spore-band" style={{ color: BAND_COLOR[snap.band] }}>{snap.band}</b></div>
    <div className="mt-2 h-6 border border-white/10"><div data-testid="spore-activity-bar" className="h-full transition-[width] duration-100" style={{ width: `${Math.round(snap.activity * 100)}%`, background: BAND_COLOR[snap.band], boxShadow: `0 0 24px ${BAND_COLOR[snap.band]}` }} /></div>
    <div className="mt-1 flex justify-between text-[0.6rem] tracking-[0.3em] text-[var(--dim)]"><span>NONE</span><span>LOW</span><span>MID</span><span>HIGH</span></div>
  </div>
);

const LevelBanner = ({ snap }) => {
  const f = snap.levelFeeling;
  const color = FEELING_COLORS3[f];
  if (!['instruct', 'try'].includes(snap.phase)) return null;
  return (
    <div data-testid="level-banner" className="pointer-events-none absolute inset-x-0 top-[22%] flex flex-col items-center">
      <div data-testid="level-title" className="font-display text-5xl font-extrabold tracking-[0.12em] lg:text-7xl" style={{ color, textShadow: `0 0 40px ${color}` }}>LEVEL {snap.level}/6 · {f.toUpperCase()}</div>
      <div className="mt-6 h-5 w-[min(900px,70vw)] border border-white/20"><div data-testid="level-progress-bar" className="h-full transition-[width] duration-150" style={{ width: `${Math.round(snap.levelProgress * 100)}%`, background: color, boxShadow: `0 0 30px ${color}` }} /></div>
      <div data-testid="level-progress-value" className="mt-2 text-sm tracking-[0.4em] text-[var(--muted)]">{snap.phase === 'try' ? `${Math.round(snap.levelProgress * 100)}%${snap.eased ? ' · EASED' : ''}` : 'LISTEN TO ZE PROFESSEUR…'}</div>
    </div>
  );
};

export const LevelAudienceHud = ({ snap }) => (
  <div data-testid="audience-hud" className="pointer-events-none absolute inset-0 flex flex-col justify-between p-10">
    <div className="flex items-start justify-between">
      <div>
        <div className="label-eyebrow">[ SPECIMEN: THE SPORE<span className="normal-case">s</span> ]</div>
        <div className="font-display mt-1 text-2xl font-extrabold">SPORE Language Assessment - Feelings.</div>
        <div data-testid="stage-phase-label" className="label-eyebrow mt-2">{snap.phase === 'opening' ? 'INTRODUCTION' : snap.phase === 'try' ? 'YOUR TURN' : snap.phase.toUpperCase()}</div>
      </div>
      <LevelTracker snap={snap} />
    </div>
    <LevelBanner snap={snap} />
    <div className="flex items-end justify-between gap-6">
      <SporeResponse snap={snap} />
      <div className="max-w-[720px] flex-1"><Transmission tx={snap.phase === 'try' && snap.ui.transmission && snap.ui.transmission.ended ? null : snap.ui.transmission} /></div>
      <MiniScope />
    </div>
  </div>
);

export const UnlockOverlay = ({ snap }) => {
  const f = snap.levelFeeling;
  const color = FEELING_COLORS3[f];
  return (
    <div data-testid="unlock-overlay" className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
      <div className="text-sm tracking-[0.6em] text-[var(--ink)]">SIGNAL DECODED · THE SPECIMEN RESPONDS…</div>
      <div data-testid="unlock-title" className="font-display mt-4 text-6xl font-extrabold tracking-[0.1em] lg:text-8xl" style={{ color, textShadow: `0 0 60px ${color}, 0 0 120px ${color}` }}>EXPERIMENT {snap.level}</div>
      <div className="font-display mt-2 text-4xl font-extrabold tracking-[0.5em]" style={{ color }}>— DECODED —</div>
    </div>
  );
};

export const LevelHostHud = ({ snap, lab }) => {
  const st = lab.status;
  const b = snap.touch.baseline;
  return (
    <div data-testid="host-hud" className="pointer-events-none absolute right-6 top-36 w-[360px] border border-[var(--hair-strong)] bg-[#050807]/90 p-4 text-xs backdrop-blur-md">
      <div className="mb-2 tracking-[0.2em]">HOST · {st.status.toUpperCase()} {st.deviceName ? `· ${st.deviceName}` : ''}</div>
      {snap.hostHint && snap.phase === 'try' && <div data-testid="host-prompt" className="mb-2 border border-[var(--amber)] p-2 text-center text-sm text-[var(--amber)] dot-pulse">N TO UNLOCK</div>}
      <Row k="Phase" v={`${snap.phase}${snap.paused ? ' (paused)' : ''}`} testId="host-phase" />
      <Row k="Level" v={`${snap.level}/6 ${snap.levelFeeling} · ${Math.round(snap.levelProgress * 100)}%`} testId="host-level" />
      <Row k="Needs" v={`${snap.levelHint}${snap.eased ? ' (eased -25%)' : ''}`} />
      <Row k="Trying for" v={`${snap.tryS.toFixed(0)} s · nudge ${snap.nudged ? 'used' : 'available'}`} testId="host-timer" warn={snap.tryS > 45} />
      {snap.levelFeeling === 'hate' && <Row k="Jabs" v={snap.burstCount} testId="host-bursts" />}
      <div className="mt-2 text-[var(--muted)]">Signal health</div>
      <Row k="Activity / band" v={`${snap.activity.toFixed(2)} · ${snap.band}`} testId="host-band" />
      <Row k="Motion / baseline" v={`${snap.touch.features.raw.toFixed(0)} / ${b.median.raw.toFixed(0)} ± ${b.spread.toFixed(0)}`} testId="host-baseline" />
      <Row k="Baseline age · re-baselines" v={snap.touch.learning ? `LEARNING ${(snap.touch.learnRemainingMs / 1000).toFixed(1)}s` : `${b.ageS.toFixed(0)} s · ${b.rebaselines}`} testId="host-rebaselines" warn={b.rebaselines > 3} />
      <Row k="Touch sensitivity" v={`${snap.sensitivity}/5`} testId="host-strictness" />
      <div className="mt-2 text-[var(--muted)]">Media</div>
      <Row k="Voices" v={`${snap.preload.voices}/${snap.preload.voicesAvailable}${snap.preload.voicesFailed ? ` · ${snap.preload.voicesFailed} failed` : ''}`} warn={snap.preload.voicesFailed > 0} testId="host-voices" />
      <Row k={<>SPORE<span className="normal-case">s</span> SFX</>} v={`${snap.preload.sfx}/${snap.preload.sfxAvailable}`} warn={snap.preload.sfx < snap.preload.sfxAvailable} testId="host-sfx" />
      <Row k="Audio" v={snap.muted ? 'MUTED' : snap.audioReady ? 'on' : 'locked (press Space)'} warn={snap.muted || !snap.audioReady} />
      <Row k="Simulator" v={lab.sim.enabled ? 'ON' : 'off'} warn={lab.sim.enabled} />
    </div>
  );
};

const KEYS = [
  ['Space', 'start / pause'], ['N', 'unlock current level'], ['1–6', 'jump to level'], ['H', 'nudge now'],
  ['↑ / ↓', 'level bar ±10%'], ['B', 're-learn base state'], ['X', 'save 60 s raw snapshot'], ['C', 'calibrate'], ['[ / ]', 'touch sensitivity'],
  ['M', 'mute'], ['D', 'host HUD'], ['?', 'this sheet'], ['F', 'fullscreen'], ['L', 'signal lab'], ['G / Shift+G', 'new script / repair'], ['R ×2', 'reset'],
  ['S', 'simulator on/off'], ['A / Q / E', 'hold: LOW / MID / HIGH'], ['W', 'short burst (jab)'],
];
export const LevelCheatSheet = () => (
  <div data-testid="cheat-sheet" className="pointer-events-none absolute inset-0 flex items-center justify-center bg-[#050807]/80 backdrop-blur-md">
    <div className="grid grid-cols-2 gap-x-12 gap-y-3 border border-[var(--hair-strong)] p-10 text-lg">
      {KEYS.map(([k, d]) => (
        <div key={k} className="flex items-center gap-4"><span className="kbd min-w-[5.5rem] px-2">{k}</span><span className="text-[var(--muted)]">{d}</span></div>
      ))}
    </div>
  </div>
);
